import { useMemo, useState } from "react";
import { Input, Collapse, Tag } from "antd";
import AIOffBadge from "./AIOffBadge";

/**
 * Read-only catalog of reusable clinical-note sections.
 *
 * Backend source: GET /api/v1/templates/library (lands in backend PR #12 part 2).
 * For now consumes the optional `catalog` prop (the parent page fetches it once)
 * and falls back to a tiny inline placeholder so the component renders in isolation.
 */
export default function SectionLibrary({ catalog, onAddSection, specialtyTag, addedSectionIds }) {
  const [filter, setFilter] = useState("");

  // A section already on the canvas can't be added again (backend UNIQUE
  // constraint on (template_version_id, section_id)); surface that in the UI.
  const addedIds = useMemo(() => new Set(addedSectionIds || []), [addedSectionIds]);

  const sections = catalog?.sections || PLACEHOLDER_CATALOG.sections;
  const categories = catalog?.categories || PLACEHOLDER_CATALOG.categories;

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.display_order - b.display_order),
    [categories]
  );

  const sectionsByCategory = useMemo(() => {
    const grouped = {};
    for (const s of sections) {
      // Specialty filter: show if universal (no tags) or tags include the current specialty.
      const tags = s.specialty_tags;
      const matchesSpecialty =
        !specialtyTag ||
        !tags ||
        tags.length === 0 ||
        tags.includes(specialtyTag);
      if (!matchesSpecialty) continue;

      const matchesFilter =
        !filter ||
        s.display_name.toLowerCase().includes(filter.toLowerCase()) ||
        s.section_id.toLowerCase().includes(filter.toLowerCase());
      if (!matchesFilter) continue;

      grouped[s.category] = grouped[s.category] || [];
      grouped[s.category].push(s);
    }
    return grouped;
  }, [sections, filter, specialtyTag]);

  return (
    <aside className="w-72 border-r border-gray-200 bg-gray-50 flex flex-col">
      <div className="p-3 border-b border-gray-200">
        <h3 className="font-semibold text-sm text-gray-700 mb-1">Section Library</h3>
        <p className="text-xs text-gray-400 mb-2">Click a section to add it to the template.</p>
        <Input.Search
          placeholder="Filter sections"
          allowClear
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <Collapse
          ghost
          defaultActiveKey={sortedCategories.map((c) => c.id)}
          items={sortedCategories.map((category) => ({
            key: category.id,
            label: <span className="text-xs uppercase tracking-wide text-gray-500">{category.display_name}</span>,
            children: (
              <ul className="space-y-1">
                {(sectionsByCategory[category.id] || []).map((s) => {
                  const added = addedIds.has(s.section_id);
                  return (
                    <li
                      key={s.section_id}
                      className={`px-2 py-1.5 rounded text-sm flex items-center justify-between ${
                        added
                          ? "opacity-40 cursor-not-allowed"
                          : "hover:bg-blue-50 cursor-grab"
                      }`}
                      onClick={() => (added ? null : onAddSection?.(s))}
                      role="button"
                      tabIndex={added ? -1 : 0}
                      aria-disabled={added}
                      title={added ? "Already added to this template" : undefined}
                      onKeyDown={(e) =>
                        e.key === "Enter" && !added ? onAddSection?.(s) : null
                      }
                    >
                      <span>{s.display_name}</span>
                      {s.default_classification === "clinician_controlled" && (
                        <AIOffBadge />
                      )}
                    </li>
                  );
                })}
              </ul>
            ),
          }))}
        />
      </div>
    </aside>
  );
}

/** Placeholder catalog used in dev when the API isn't available yet. */
const PLACEHOLDER_CATALOG = {
  categories: [
    { id: "narrative", display_name: "Narrative", display_order: 1 },
    { id: "mental_status_and_safety", display_name: "Mental Status & Safety", display_order: 5 },
  ],
  sections: [
    {
      section_id: "chief_complaint",
      display_name: "Chief Complaint",
      category: "narrative",
      default_classification: "ai_driven",
    },
    {
      section_id: "history_of_present_illness",
      display_name: "History of Present Illness",
      category: "narrative",
      default_classification: "ai_driven",
    },
    {
      section_id: "suicide_risk_assessment",
      display_name: "Suicide Risk Assessment",
      category: "mental_status_and_safety",
      default_classification: "clinician_controlled",
    },
  ],
};
